import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, IntlProvider } from 'react-intl';
import { LANGUAGES } from '../_i18n/languages';
import translations from '../_i18n/translations.json';

const LANG_METADATA = {
  en: { name: 'English', flag: '🇺🇸', switchBtn: 'Switch to English', dismissBtn: 'Not now' },
  pt: { name: 'Português', flag: '🇧🇷', switchBtn: 'Mudar para Português', dismissBtn: 'Não, obrigado' },
  es: { name: 'Español', flag: '🇪🇸', switchBtn: 'Cambiar a Español', dismissBtn: 'No, gracias' },
  fr: { name: 'Français', flag: '🇫🇷', switchBtn: 'Passer en Français', dismissBtn: 'Pas maintenant' },
  it: { name: 'Italiano', flag: '🇮🇹', switchBtn: 'Passa a Italiano', dismissBtn: 'Non ora' },
  ru: { name: 'Русский', flag: '🇷🇺', switchBtn: 'Перейти на Русский', dismissBtn: 'Не сейчас' },
  de: { name: 'Deutsch', flag: '🇩🇪', switchBtn: 'Zu Deutsch wechseln', dismissBtn: 'Nicht jetzt' },
  nl: { name: 'Nederlands', flag: '🇳🇱', switchBtn: 'Naar Nederlands', dismissBtn: 'Niet nu' }
};

class LangCheck extends Component {
  state = {
    showLangNotify: false,
    userLanguage: null,
    countdown: 15,
    totalDuration: 15,
    isPaused: false
  };

  countdownInterval = null;
  mountTimeout = null;

  componentDidMount() {
    if (typeof window === 'undefined') return;

    try {
      if (sessionStorage.getItem('timenowin_lang_dismissed') === 'true') {
        return;
      }
    } catch (e) {}

    try {
      const detectBrowserLanguage = require('detect-browser-language');
      const detectedRaw = String(detectBrowserLanguage() || '').toLowerCase().split('-')[0];
      
      if (detectedRaw && LANGUAGES[detectedRaw]) {
        this.setState({ userLanguage: detectedRaw }, () => {
          if (!this.checkLanguage()) {
            this.mountTimeout = setTimeout(() => {
              this.setState({ showLangNotify: true, countdown: 15 }, () => {
                this.startTimer();
              });
            }, 1200);
          }
        });
      }
    } catch (err) {
      // Ignora erro em ambientes restritos
    }
  }

  componentWillUnmount() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    if (this.mountTimeout) {
      clearTimeout(this.mountTimeout);
    }
  }

  startTimer = () => {
    if (this.countdownInterval) clearInterval(this.countdownInterval);
    this.countdownInterval = setInterval(() => {
      if (this.state.isPaused) return;

      this.setState((prevState) => {
        if (prevState.countdown <= 1) {
          clearInterval(this.countdownInterval);
          this.handleDismiss();
          return { countdown: 0, showLangNotify: false };
        }
        return { countdown: prevState.countdown - 1 };
      });
    }, 1000);
  };

  checkLanguage() {
    const usrLang = this.state.userLanguage;
    if (!usrLang || !LANGUAGES[usrLang]) return true;

    const pathname = this.props.location?.pathname || '/';
    const pathParts = pathname.split('/').filter(Boolean);
    const routeLang = (pathParts.length > 0 && LANGUAGES[pathParts[0]]) ? pathParts[0] : 'en';

    return usrLang === routeLang;
  }

  getTargetUrl(targetLang) {
    const pathname = this.props.location?.pathname || '/';
    const pathParts = pathname.split('/').filter(Boolean);

    let restOfPath = '';
    if (pathParts.length > 0 && LANGUAGES[pathParts[0]]) {
      restOfPath = pathParts.slice(1).join('/');
    } else {
      restOfPath = pathParts.join('/');
    }

    const search = this.props.location?.search || '';
    let targetUrl = '';
    if (targetLang === 'en') {
      targetUrl = restOfPath ? `/${restOfPath}` : '/';
    } else {
      targetUrl = restOfPath ? `/${targetLang}/${restOfPath}` : `/${targetLang}`;
    }

    return targetUrl + search;
  }

  handleDismiss = () => {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    this.setState({ showLangNotify: false });
    try {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('timenowin_lang_dismissed', 'true');
      }
    } catch (e) {}
  };

  handleSwitch = (e) => {
    e.preventDefault();
    const { userLanguage } = this.state;
    if (!userLanguage) return;

    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    const fullUrl = this.getTargetUrl(userLanguage);
    if (typeof window !== 'undefined') {
      window.location.href = fullUrl;
    } else if (this.props.history?.push) {
      this.props.history.push(fullUrl);
    }
  };

  render() {
    const { showLangNotify, userLanguage, countdown, totalDuration, isPaused } = this.state;
    if (!showLangNotify || !userLanguage || this.checkLanguage()) {
      return null;
    }

    const langInfo = LANG_METADATA[userLanguage] || {
      name: userLanguage.toUpperCase(),
      flag: '🌐',
      switchBtn: 'Change Language',
      dismissBtn: 'Dismiss'
    };
    const messages = translations[LANGUAGES[userLanguage]?.code] || translations['en-US'];
    const progressPercent = Math.max(0, Math.min(100, (countdown / totalDuration) * 100));

    return (
      <aside
        className="lang-floating-toast"
        role="dialog"
        aria-label="Language suggestion"
        onMouseEnter={() => this.setState({ isPaused: true })}
        onMouseLeave={() => this.setState({ isPaused: false })}
      >
        <div
          className="lang-toast-progress-bar"
          style={{
            width: `${progressPercent}%`,
            opacity: isPaused ? 0.6 : 1
          }}
          title={isPaused ? "Pausado" : `Tempo restante: ${countdown}s`}
        />

        <div className="lang-toast-header">
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <div className="lang-toast-badge">
              <span className="lang-toast-flag" role="img" aria-label={langInfo.name}>{langInfo.flag}</span>
              <span className="lang-toast-name">{langInfo.name}</span>
            </div>
            <span className="lang-toast-timer" title={isPaused ? "Timer pausado" : "Tempo para resposta"}>
              {isPaused ? '⏸️' : '⏱️'} {countdown}s
            </span>
          </div>
          <button
            type="button"
            className="lang-toast-close"
            onClick={this.handleDismiss}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <IntlProvider locale={userLanguage} messages={messages}>
          <div className="lang-toast-body">
            <FormattedMessage id="body.lang.suggest" defaultMessage="Access here in language" />{' '}
            <strong>{langInfo.name}</strong>?
          </div>
        </IntlProvider>

        <div className="lang-toast-actions">
          <button
            type="button"
            className="lang-toast-btn-dismiss"
            onClick={this.handleDismiss}
          >
            {langInfo.dismissBtn} ({countdown}s)
          </button>
          <a
            href={this.getTargetUrl(userLanguage)}
            className="lang-toast-btn-switch"
            onClick={this.handleSwitch}
          >
            <span>{langInfo.switchBtn}</span>
          </a>
        </div>
      </aside>
    );
  }

  static pathLocation(props, country) {
    let aPath = props.location?.pathname?.split('/') || [];
    if (aPath[1] && aPath[1].length === 2 && aPath[2]) {
      return `/${country}${country ? '/' : 'en/'}${aPath[2]}`;
    }
    return `/${country}`;
  }
}

export default withRouter(LangCheck);